// A single (doctor × weekday) cell's mini day-plan: a vertical time strip with
// horizontal hour grid lines and one colored band per slot. The day axis comes
// from the `scale` prop so the matrix can swap between a working-hours window
// and a full 24-hour view. Also exports `HourRulerMini` for the sticky header.

import { dayCellGeometry, monthlyWeekSegments, slotColor } from "./heatmap-utils";

// Hairline shared by solid bars and segment chips so adjacent same-color slots
// keep a visible edge.
const SLOT_BAND_BOX_SHADOW = "inset 0 0 0 1px oklch(0.20 0.025 264 / 0.6)";

// Position (% along the axis) of an hour mark within the given day bounds.
function axisPct(hour, startMin, endMin) {
  return ((hour * 60 - startMin) / (endMin - startMin)) * 100;
}

// The vertical day-plan for one cell. `rows` are the slot rows that fall on this
// cell's weekday; `tenantHue` colors bands that have no explicit `colorHex`;
// `scale` defines the day-axis bounds and grid hours.
export function DayColumn({ rows = [], tenantHue, scale }) {
  const { startMin, endMin, gridHours } = scale;
  return (
    <div className="relative h-full w-full overflow-hidden rounded-[3px] bg-black/5 dark:bg-muted/70">
      {gridHours.map((hour) => (
        <span
          key={hour}
          aria-hidden
          className="absolute inset-x-0 h-px bg-muted-foreground/15"
          style={{ top: `${axisPct(hour, startMin, endMin)}%` }}
        />
      ))}
      {rows.map((row, idx) => {
        const { topPct, heightPct } = dayCellGeometry(row, startMin, endMin);
        const key = row.definitionId ?? row.id ?? idx;
        const segments = monthlyWeekSegments(row);
        const fill = slotColor(row, tenantHue);
        // Default render: one solid band spanning the full cell width. Used for
        // static, weekly, monthly day-of-month, and dates recurrences.
        if (!segments) {
          return (
            <span
              key={key}
              className="absolute inset-x-0.5 rounded-[2px]"
              style={{
                top: `${topPct}%`,
                height: `${heightPct}%`,
                background: fill,
                boxShadow: SLOT_BAND_BOX_SHADOW,
              }}
            />
          );
        }
        // Monthly Nth-weekday: 5 chips at 20% width each, one per week-of-month
        // bucket. Inactive buckets stay transparent so the cell background
        // shows through and the "which weeks" reads at a glance.
        return (
          <div
            key={key}
            className="absolute inset-x-0.5"
            style={{ top: `${topPct}%`, height: `${heightPct}%` }}
          >
            {segments.map((on, i) => (
              <span
                key={i}
                aria-hidden={!on}
                className="absolute top-0 h-full rounded-[2px]"
                style={{
                  left: `calc(${i * 20}% + 1px)`,
                  width: `calc(20% - 2px)`,
                  background: on ? fill : "transparent",
                  boxShadow: on ? SLOT_BAND_BOX_SHADOW : undefined,
                }}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}

// The thin ruler bar in the matrix header's second row — vertical ticks as a
// reminder of each cell's time axis. Tick hours come from `scale`.
export function HourRulerMini({ scale }) {
  const { startMin, endMin, rulerHours } = scale;
  return (
    <div className="relative h-3 w-full overflow-hidden rounded-[3px] bg-muted/40">
      {rulerHours.map((hour) => (
        <span
          key={hour}
          aria-hidden
          className="absolute top-0 h-full w-px bg-muted-foreground/40"
          style={{ left: `${axisPct(hour, startMin, endMin)}%` }}
        />
      ))}
    </div>
  );
}
